import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Standard text input.
 *
 * Visual: tertiary chip (`bg-foreground/5`) with no rest border. Hover deepens
 * the fill; focus reveals a 2px accent ring on the wrapper. Matches the
 * design language used for buttons and badges so a form reads as a row of
 * tinted chips rather than a stack of bordered surfaces.
 *
 * Optional `prefix` / `suffix` render as muted add-ons inside the same
 * wrapper, sharing one focus ring (e.g. `<Input suffix=".lndng.dev" />`).
 */
export interface InputProps extends Omit<React.ComponentProps<'input'>, 'prefix' | 'suffix'> {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, prefix, suffix, disabled, ...props }, ref) => {
    return (
      <div
        className={cn(
          'group flex h-9 w-full items-stretch rounded text-sm',
          'bg-foreground/5 [&:hover:not(:focus-within)]:bg-foreground/10',
          'border-2 border-transparent',
          '[&:focus-within]:!bg-surface [&:focus-within]:border-foreground',
          'transition-colors',
          disabled && 'cursor-not-allowed opacity-40 pointer-events-none',
          className,
        )}
      >
        {prefix !== undefined && (
          <span className="flex items-center pl-3 pr-1 text-muted-foreground select-none">
            {prefix}
          </span>
        )}
        <input
          type={type}
          ref={ref}
          disabled={disabled}
          className={cn(
            'min-w-0 flex-1 bg-transparent px-3 text-foreground',
            'outline-none border-0',
            'placeholder:text-muted-foreground',
            'disabled:cursor-not-allowed',
            'file:border-0 file:bg-transparent file:font-medium file:text-foreground',
          )}
          {...props}
        />
        {suffix !== undefined && (
          <span className="flex items-center pl-1 pr-3 text-muted-foreground select-none">
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
